import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { unwrap, type Deca } from '@deca/api'

import { api } from '../app/api.ts'
import { apiErrorMessage } from '../app/errors.ts'
import { useDeadAddresses } from '../app/queries.ts'
import { isEmailAddress } from '../deca/emailAddress.ts'
import { MAX_DECA_RECIPIENTS } from '../deca/emailRecipients.ts'
import { Dialog } from './Dialog.tsx'
import { EmailTagsInput } from './EmailTagsInput.tsx'
import { Button, Notice } from './primitives.tsx'

/**
 * «Enviar por correo» (#219): the recipients reviewed — the DeCA's own list to start with — then
 * «Enviar». Each recipient gets a letter of their own. What is sent becomes the DeCA's list; the
 * shipper's book entry stays as it is.
 *
 * One `Idempotency-Key` per opening of the dialog: a double click, or a retry after an answer that
 * never arrived, is the same sending.
 */
export function EmailSendDialog({
  deca,
  onClose,
  onSent,
}: {
  deca: Deca
  onClose: () => void
  onSent: (count: number) => void
}) {
  const { t } = useTranslation()
  const [recipients, setRecipients] = useState<string[]>(() => [...(deca.emailRecipients ?? [])].slice(0, MAX_DECA_RECIPIENTS))
  const [key] = useState(() => crypto.randomUUID())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dead = useDeadAddresses(recipients)

  const invalid = recipients.some((address) => !isEmailAddress(address))
  const deliverable = recipients.filter((address) => !dead.has(address.toLowerCase()))

  async function send() {
    if (invalid || recipients.length === 0) return
    setBusy(true)
    setError(null)
    try {
      await unwrap(
        api.POST('/api/v1/decas/{decaId}/emails', {
          params: { path: { decaId: deca.id }, header: { 'Idempotency-Key': key } },
          body: { recipients },
        }),
      )
      onSent(recipients.length)
    } catch (caught) {
      setError(apiErrorMessage(caught, t))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      title={t('emails.dialog.title')}
      subtitle={t('emails.dialog.subtitle', { documentNumber: deca.documentNumber ?? '' })}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="mail"
            loading={busy}
            disabled={busy || invalid || recipients.length === 0}
            onClick={() => void send()}
          >
            {t('emails.dialog.send')}
          </Button>
        </>
      }
    >
      <div className="stack">
        {error ? <Notice tone="danger">{error}</Notice> : null}
        <EmailTagsInput
          label={t('create.email.recipients')}
          max={MAX_DECA_RECIPIENTS}
          value={recipients}
          dead={dead}
          onChange={setRecipients}
        />
        {recipients.length > 0 && deliverable.length < recipients.length ? (
          <Notice tone="warning">{t('emails.dialog.someDead', { count: recipients.length - deliverable.length })}</Notice>
        ) : null}
        <p className="small muted">{t('emails.dialog.whatIsSent')}</p>
      </div>
    </Dialog>
  )
}
